/**
 * Thin fetch wrapper. Sessions are cookie-based and HttpOnly, so all we
 * need on the client is `credentials: 'include'` + standard JSON handling.
 *
 * On 401, callers should redirect to /login (handled in App.tsx via the
 * `useMe` hook). We don't auto-redirect from inside `apiFetch` because some
 * endpoints (`/api/me` itself) treat a 401 as just "user not signed in"
 * and need to render a different UI.
 */

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  const text = await res.text();
  let body: unknown = text;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    /* keep as text */
  }
  if (!res.ok) {
    const message =
      (body as any)?.error ?? (body as any)?.message ?? `HTTP ${res.status}`;
    throw new ApiError(message, res.status, body);
  }
  return body as T;
}
